import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ICONS, Preview } from '@app/styles';
import { Button, ComponentSync, EditableDBItemV3, ModuleFE_Toaster, TS_ErrorBoundary, TS_Tabs } from '@nu-art/thunderstorm-frontend';
import { ModuleFE_Expression } from '../../../../_entity.js';
import { exists, MUSTNeverHappenException, scrub } from '@nu-art/ts-common';
import { ModuleFE_FloatingWindows, windowRect } from '@nu-art/floating-windows';
import { Component_ExpressionPrettyPrinter, Component_JSONPreview, Component_PyJsonPreview } from '../../../../expression.js';
import { Editor_DynamicExpression } from '../Editor_DynamicExpression/Editor_DynamicExpression.js';
import './Window_ExpressionEditor.scss';
function isRightHandEmpty(rh) {
    if (!rh)
        return true;
    return Array.isArray(rh) && (rh.length === 0 || rh.every(v => v === undefined || v === null));
}
function validateAtom(atom) {
    const errors = {};
    if (!atom.leftHand?.id)
        errors.leftHand = 'Variable is required';
    const firstCase = atom.cases?.[0];
    if (!firstCase?.caseOperator)
        errors.cases = [{ caseOperator: 'Operator is required' }];
    else if (isRightHandEmpty(firstCase.rightHand))
        errors.cases = [{ rightHand: 'Value is required' }];
    return Object.keys(errors).length ? errors : undefined;
}
function validateTerms(terms) {
    if (!terms.length)
        return undefined;
    const termErrors = [];
    let hasErrors = false;
    terms.forEach((term, tIdx) => {
        const tErr = {};
        if (term.atomsLogic) {
            const atomErrors = [];
            term.atoms?.forEach((atom, aIdx) => {
                const err = validateAtom(atom);
                if (!err)
                    return;
                atomErrors[aIdx] = err;
                hasErrors = true;
            });
            if (atomErrors.length)
                tErr.atoms = atomErrors;
        }
        if (Object.keys(tErr).length)
            termErrors[tIdx] = tErr;
    });
    if (!hasErrors)
        return undefined;
    return { errors: termErrors, missing: 'Term fields (Variable, Operator, Value)' };
}
function validateNewExpressionItem(item) {
    const results = {};
    const missing = [];
    if (!item.label?.trim()) {
        results.label = 'Label is required';
        missing.push('Label');
    }
    const termsResult = validateTerms(item.terms ?? []);
    if (termsResult) {
        results.terms = termsResult.errors;
        missing.push(termsResult.missing);
    }
    if (!item.outputType) {
        results.outputType = 'Output Type is required';
        missing.push('Output Type');
    }
    if (!item.expression?.trim()) {
        results.expression = 'Expression is required';
        missing.push('Expression (E:)');
    }
    return Object.keys(results).length ? { results, missing } : undefined;
}
export class Window_ExpressionEditor extends ComponentSync {
    static show = (props) => {
        const model = {
            key: `preview-${props.expressionId ?? 'new-expression'}`,
            className: 'preview-window window__expression-editor',
            rect: { ...windowRect.thirdScreen_Left(), width: undefined },
            moveable: true,
            resizable: true,
            content: (cb) => _jsx(Window_ExpressionEditor, { ...props, onClose: cb })
        };
        ModuleFE_FloatingWindows.window.add(model);
    };
    //######################### Life Cycle #########################
    deriveStateFromProps(props, state) {
        const isNew = !props.expressionId;
        const currentId = state.editable?.item._id;
        const propsId = props.expressionId;
        if (!state.editable || (exists(propsId) && currentId !== propsId)) {
            state.editable = this.getEditable(propsId, isNew, props.initialData);
            state.isNewExpression = isNew;
        }
        state.editMode = !!props.editMode;
        state.editorConfig = props.editorConfig ?? Editor_DynamicExpression.Config_NoEditing;
        state.selectedTab ??= 'editor';
        state.label ??= state.editable.item.label;
        return state;
    }
    //######################### Logic #########################
    getEditable(expressionId, isNew, initialData) {
        const expression = ModuleFE_Expression.cache.unique(expressionId);
        if (exists(expressionId) && !exists(expression))
            throw new MUSTNeverHappenException(`Could not find expression for id ${expressionId}`);
        const data = expression ?? (initialData ? { ...initialData } : {});
        const editable = new EditableDBItemV3(data, ModuleFE_Expression)
            .setPreSaveAction(item => scrub(item, { emptyObjects: false, emptyArrays: false, returnCopy: false }))
            .setOnChanged(async () => this.forceUpdate())
            .setOnSaveCompleted(item => {
            if (isNew) {
                this.props.onSaved?.(item);
                this.props.onClose();
            }
            else {
                this.setState({ editable: this.getEditable(item._id), isNewExpression: false });
            }
        });
        if (!isNew)
            editable.setAutoSave(true);
        return editable;
    }
    saveNewExpression = async () => {
        const validation = validateNewExpressionItem(this.state.editable.item);
        if (validation) {
            this.state.editable.validationResults = { autoSave: false, editing: true, results: validation.results };
            ModuleFE_Toaster.toastError(`Missing required fields: ${validation.missing.join(', ')}`);
            this.forceUpdate();
            return;
        }
        this.state.editable.validationResults = undefined;
        await this.state.editable.save();
    };
    getTabs = () => {
        return [
            {
                uid: 'editor',
                title: 'Editor',
                content: () => _jsx(Editor_DynamicExpression, { editable: this.state.editable, editMode: this.state.editMode, config: this.state.editorConfig })
            },
            {
                uid: 'pretty-print',
                title: 'Pretty Print',
                content: () => _jsx(TS_ErrorBoundary, { children: _jsx(Component_ExpressionPrettyPrinter, { editable: this.state.editable }) }),
            },
            {
                uid: 'json',
                title: 'JSON',
                content: () => _jsx(Component_JSONPreview, { editable: this.state.editable }),
            },
            {
                uid: 'py-json',
                title: 'PY JSON',
                content: () => _jsx(Component_PyJsonPreview, { editable: this.state.editable })
            }
        ];
    };
    //######################### Render #########################
    render() {
        return _jsxs(Preview.Root, { children: [this.render_Header(), this.render_Main()] });
    }
    render_Header = () => {
        return _jsxs(Preview.Header, { children: [_jsx(Preview.HeaderTitle, { children: this.state.editable.item.label ?? 'New Expression' }), _jsxs(Preview.HeaderTail, { children: [this.state.isNewExpression && _jsx(Button, { variant: 'primary', onClick: this.saveNewExpression, children: "Save" }), _jsx(ICONS.advisor_avn_close.component, { onClick: this.props.onClose })] })] });
    };
    render_Main = () => {
        const tabs = this.getTabs();
        return _jsx(Preview.Main, { children: _jsx(TS_Tabs, { tabs: tabs, selectedTabId: this.state.selectedTab, onSelected: tab => this.setState({ selectedTab: tab }) }) });
    };
}
//# sourceMappingURL=Window_ExpressionEditor.js.map